import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "./Admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, ArrowLeft, RefreshCw, Undo2, Download, Clock } from "lucide-react";

type Order = {
  id: string;
  customer_email: string;
  customer_name: string | null;
  status: string;
  payment_status: string;
  total: number;
  subtotal: number;
  stripe_session_id: string | null;
  stripe_payment_intent: string | null;
  created_at: string;
};
type OrderItem = {
  id: string;
  product_name: string;
  variant_label: string | null;
  quantity: number;
  unit_price: number;
};

const fmtMoney = (n: number) => `$${Number(n).toFixed(2)}`;

const statusVariant = (s: string): "default" | "secondary" | "outline" | "destructive" => {
  if (s === "paid") return "default";
  if (s === "pending") return "secondary";
  if (s === "refunded" || s === "cancelled") return "destructive";
  return "outline";
};

export const AdminOrders = () => {
  const [rows, setRows] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("orders")
      .select("id,customer_email,customer_name,status,payment_status,total,subtotal,stripe_session_id,stripe_payment_intent,created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    setRows((data ?? []) as Order[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) =>
    !q || r.customer_email.toLowerCase().includes(q.toLowerCase()) || r.id.includes(q),
  );

  const exportCsv = () => {
    const head = ["id","created_at","email","name","status","payment_status","total"];
    const lines = [head.join(",")].concat(
      filtered.map((r) => [r.id, r.created_at, r.customer_email, r.customer_name ?? "", r.status, r.payment_status, r.total].map((v) => `"${String(v ?? "").replace(/"/g,'""')}"`).join(",")),
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `orders-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="ORDERS">
      <div className="flex items-center gap-3 mb-6">
        <Input placeholder="Search email or ID…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
        <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        <Button variant="outline" onClick={exportCsv} className="gap-2"><Download className="w-4 h-4" /> CSV</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground font-body text-sm">No orders yet.</p>
      ) : (
        <div className="bg-panel border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left font-mono uppercase text-xs text-muted-foreground">
                <th className="p-3">Date</th><th className="p-3">Customer</th><th className="p-3">Status</th><th className="p-3">Payment</th><th className="p-3 text-right">Total</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0">
                  <td className="p-3 font-mono text-xs">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <div>{r.customer_email}</div>
                    {r.customer_name && <div className="text-xs text-muted-foreground">{r.customer_name}</div>}
                  </td>
                  <td className="p-3"><Badge variant={statusVariant(r.status)}>{r.status}</Badge></td>
                  <td className="p-3"><Badge variant={statusVariant(r.payment_status)}>{r.payment_status}</Badge></td>
                  <td className="p-3 text-right font-display text-primary">{fmtMoney(r.total)}</td>
                  <td className="p-3 text-right">
                    <Button asChild size="sm" variant="outline"><Link to={`/admin/orders/${r.id}`}>Open</Link></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

export const AdminOrderDetail = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [recons, setRecons] = useState<any[]>([]);
  const [audit, setAudit] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: o }, { data: oi }, { data: ev }, { data: rc }, { data: al }] = await Promise.all([
      supabase.from("orders").select("*").eq("id", id).maybeSingle(),
      supabase.from("order_items").select("*").eq("order_id", id),
      supabase.from("stripe_webhook_events").select("*")
        .eq("related_type", "order").eq("related_id", id)
        .order("received_at", { ascending: false }).limit(50),
      supabase.from("order_reconciliations" as any).select("*")
        .eq("order_id", id).order("created_at", { ascending: false }).limit(50),
      supabase.from("admin_audit_log").select("*")
        .eq("entity_type", "order").eq("entity_id", id)
        .order("created_at", { ascending: false }).limit(50),
    ]);
    setOrder((o as Order) ?? null);
    setItems((oi ?? []) as OrderItem[]);
    setEvents((ev as any[]) ?? []);
    setRecons((rc as any[]) ?? []);
    setAudit((al as any[]) ?? []);
    setLoading(false);
  };
  useEffect(() => { if (id) load(); }, [id]);

  const refund = async () => {
    if (!order) return;
    if (!confirm(`Refund full order amount ${fmtMoney(order.total)}?`)) return;
    setRefunding(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-refund-order", { body: { order_id: order.id } });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success("Order refunded");
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRefunding(false);
    }
  };

  if (loading) {
    return <AdminLayout title="ORDER"><div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></AdminLayout>;
  }
  if (!order) {
    return <AdminLayout title="ORDER"><p className="text-muted-foreground">Not found.</p></AdminLayout>;
  }

  const canRefund = order.payment_status === "paid" && !!order.stripe_payment_intent;

  // Build merged timeline
  const timeline = [
    ...events.map((e) => ({ kind: "webhook", at: e.received_at ?? e.processed_at, label: e.type, status: e.status, data: e, recon: recons.find((r) => r.stripe_event_id === e.stripe_event_id) })),
    ...audit.map((a) => ({ kind: "audit", at: a.created_at, label: a.action, status: "ok", data: a, recon: null })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <AdminLayout title="ORDER">
      <button onClick={() => navigate("/admin/orders")} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to orders
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-panel border border-border p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Order</p>
              <h2 className="font-display text-2xl tracking-widest">{order.id.slice(0, 8)}</h2>
              <p className="text-xs font-mono text-muted-foreground mt-1">{new Date(order.created_at).toLocaleString()}</p>
            </div>
            <div className="flex gap-2">
              <Badge variant={statusVariant(order.status)}>{order.status}</Badge>
              <Badge variant={statusVariant(order.payment_status)}>{order.payment_status}</Badge>
            </div>
          </div>

          <h3 className="font-mono uppercase text-xs text-muted-foreground tracking-widest mb-3">Items</h3>
          <div className="border border-border">
            {items.map((it) => (
              <div key={it.id} className="flex items-center justify-between p-3 border-b border-border last:border-0 text-sm">
                <div>
                  <div>{it.product_name}</div>
                  {it.variant_label && <div className="text-xs text-muted-foreground">{it.variant_label}</div>}
                </div>
                <div className="flex items-center gap-6">
                  <span className="font-mono text-xs text-muted-foreground">× {it.quantity}</span>
                  <span className="font-display text-primary w-20 text-right">{fmtMoney(it.unit_price * it.quantity)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-4 space-y-1 flex-col items-end text-sm">
            <div>Subtotal: <span className="font-mono ml-2">{fmtMoney(order.subtotal)}</span></div>
            <div className="text-lg font-display">Total: <span className="text-primary ml-2">{fmtMoney(order.total)}</span></div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-panel border border-border p-6">
            <h3 className="font-mono uppercase text-xs text-muted-foreground tracking-widest mb-3">Customer</h3>
            <p className="text-sm">{order.customer_email}</p>
            {order.customer_name && <p className="text-xs text-muted-foreground">{order.customer_name}</p>}
          </div>

          <div className="bg-panel border border-border p-6">
            <h3 className="font-mono uppercase text-xs text-muted-foreground tracking-widest mb-3">Stripe</h3>
            <p className="text-xs font-mono break-all">{order.stripe_payment_intent ?? "—"}</p>
            <p className="text-xs text-muted-foreground font-mono break-all mt-1">{order.stripe_session_id ?? "—"}</p>
          </div>

          {canRefund && (
            <Button onClick={refund} variant="destructive" disabled={refunding} className="w-full gap-2">
              {refunding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
              Refund full amount
            </Button>
          )}
        </div>
      </div>

      <div className="bg-panel border border-border p-6 mt-6">
        <h3 className="font-mono uppercase text-xs text-muted-foreground tracking-widest mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Timeline
          <Button
            size="sm"
            variant="outline"
            className="ml-auto gap-2"
            onClick={() => {
              const head = ["at","kind","label","status","stripe_event_id","before","after","deltas","stock_changes","invoice_status"];
              const lines = [head.join(",")].concat(timeline.map((row: any) => {
                const recon = row.recon ?? {};
                const before = recon.before_snapshot ?? {};
                const after = recon.after_snapshot ?? {};
                const deltas = recon.deltas ?? {};
                const stockDelta = (deltas.stock_changes ?? deltas.stock_delta ?? "");
                const invoiceStatus = after.payment_status || row.data?.after?.payment_status || "";
                return [
                  row.at, row.kind, row.label, row.status ?? "",
                  row.data?.stripe_event_id ?? "",
                  JSON.stringify(before), JSON.stringify(after), JSON.stringify(deltas),
                  JSON.stringify(stockDelta), invoiceStatus,
                ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",");
              }));
              const blob = new Blob([lines.join("\n")], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = `order-${order.id.slice(0, 8)}-timeline.csv`; a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <Download className="w-4 h-4" /> CSV
          </Button>
        </h3>
        {timeline.length === 0 ? (
          <p className="text-xs font-mono text-muted-foreground">No webhook or admin events yet.</p>
        ) : (
          <ul className="space-y-3">
            {timeline.map((row, i) => (
              <li key={i} className="border-l-2 border-border pl-3">
                <div className="flex flex-wrap items-baseline gap-2 text-sm">
                  <Badge variant={row.kind === "webhook" ? "outline" : "secondary"}>{row.kind}</Badge>
                  <span className="font-mono text-xs">{row.label}</span>
                  <span className="ml-auto text-[10px] font-mono text-muted-foreground">{new Date(row.at).toLocaleString()}</span>
                </div>
                {row.recon && (
                  <div className="mt-2 grid grid-cols-2 gap-3 bg-background border border-border p-2 text-xs font-mono">
                    <div>
                      <div className="text-muted-foreground">BEFORE</div>
                      <pre className="overflow-x-auto">{JSON.stringify(row.recon.before_snapshot, null, 2)}</pre>
                    </div>
                    <div>
                      <div className="text-muted-foreground">AFTER</div>
                      <pre className="overflow-x-auto">{JSON.stringify(row.recon.after_snapshot, null, 2)}</pre>
                    </div>
                    <div className="col-span-2">
                      <div className="text-muted-foreground">DELTAS</div>
                      <pre className="overflow-x-auto text-primary">{JSON.stringify(row.recon.deltas, null, 2)}</pre>
                    </div>
                  </div>
                )}
                {row.kind === "audit" && row.data.after && (
                  <pre className="mt-1 bg-background border border-border p-2 text-[11px] font-mono overflow-x-auto">{JSON.stringify(row.data.after, null, 2)}</pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;